'use client';

import { useTranslations } from 'next-intl';
import type { FC, ReactNode } from 'react';
import { BaseLineInfo } from '@/app/components/icons/BaseLineInfo';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { Switch } from '@/app/components/ui/switch';
import { useStore } from '@/app/providers/store';
import type { ConfigItemOption, SubsPlanType } from '@/app/types';
import { ToolSettingsRadioGroup } from './ToolSettingsRadioGroup';

export interface ToolSettingOptionProps<T> {
  value?: T;
  onChange: (value: T) => void;
  onShouldUpgrade?: (type: 'duration' | 'resolution' | 'mode' | 'amount' | 'proMode' | 'transition' | null) => void;
  checkOptionValue?: ({ key, value, subsPlan }: { key: string; value: string; subsPlan?: SubsPlanType }) => boolean;
  className?: string;
  options: ConfigItemOption[];
  title?: ReactNode;
}

interface ToolSettingContainerProps {
  title: ReactNode;
  className?: string;
  children: ReactNode;
}

export const ToolSettingContainer: FC<ToolSettingContainerProps> = ({ title, className, children }) => {
  return (
    <div className={className}>
      <div className="flex flex-col gap-2">
        <span className="text-sm text-system-white">{title}</span>
        {children}
      </div>
    </div>
  );
};

export const AspectRatioOption: FC<ToolSettingOptionProps<string>> = ({ value, onChange, options }) => {
  return (
    <ToolSettingsRadioGroup
      value={value || ''}
      options={options.map((option) => ({
        value: option.value,
        label: option.label,
      }))}
      onValueChange={(nextValue) => {
        onChange(nextValue);
      }}
    />
  );
};

export const AspectRatioOptionSetting: FC<ToolSettingOptionProps<string>> = ({
  value,
  onChange,
  className,
  options,
}) => {
  const t = useTranslations();

  return (
    <ToolSettingContainer
      title={<span>{t('create.createForm.settings.aspectRatio.title')}</span>}
      className={className}
    >
      <div className="flex items-center gap-2">
        <AspectRatioOption value={value} onChange={onChange} options={options ?? []} />
      </div>
    </ToolSettingContainer>
  );
};

export const DurationOption: FC<ToolSettingOptionProps<number>> = ({
  value,
  onChange,
  onShouldUpgrade,
  checkOptionValue,
  options,
}) => {
  const userInfo = useStore((state) => state.userInfo);

  return (
    <ToolSettingsRadioGroup
      value={(value || '').toString()}
      options={options.map((option) => ({
        value: option.value,
        label: option.label,
        isVipOption: option.showVip,
      }))}
      onValueChange={(nextValue) => {
        if (
          checkOptionValue?.({
            key: 'duration',
            value: nextValue,
            subsPlan: userInfo?.subs_plan as SubsPlanType,
          })
        ) {
          onShouldUpgrade?.('duration');
          return;
        }

        onChange(Number.parseInt(nextValue, 10));
      }}
      renderContent={(option, isActive) => {
        return (
          <>
            {option.label}
            {isActive && 's'}
          </>
        );
      }}
    />
  );
};

export const DurationOptionSetting: FC<ToolSettingOptionProps<number>> = ({
  value,
  onChange,
  onShouldUpgrade,
  checkOptionValue,
  className,
  options,
  title,
}) => {
  const t = useTranslations();

  return (
    <ToolSettingContainer title={title || t('create.createForm.settings.duration.title')} className={className}>
      <DurationOption
        value={value}
        onChange={onChange}
        onShouldUpgrade={onShouldUpgrade}
        checkOptionValue={checkOptionValue}
        options={options ?? []}
      />
    </ToolSettingContainer>
  );
};

export const GridCountOptionSetting: FC<ToolSettingOptionProps<number>> = ({ value, onChange, className, options }) => {
  const t = useTranslations();

  return (
    <ToolSettingContainer title={t('tools.storyGrid.settings.gridCount')} className={className}>
      <ToolSettingsRadioGroup
        value={(value || '').toString()}
        options={(options ?? []).map((option) => ({
          value: option.value,
          label: option.label,
        }))}
        onValueChange={(nextValue) => {
          onChange(Number.parseInt(nextValue, 10));
        }}
      />
    </ToolSettingContainer>
  );
};

export const ResolutionOption: FC<ToolSettingOptionProps<string>> = ({
  value,
  onChange,
  onShouldUpgrade,
  checkOptionValue,
  options,
}) => {
  const t = useTranslations();
  const userInfo = useStore((state) => state.userInfo);

  return (
    <ToolSettingsRadioGroup
      value={value || ''}
      options={options.map((option) => ({
        value: option.value,
        label: option.label,
        isVipOption: option.showVip,
        badgeEle: option.remainingTrials ? (
          <div className="-top-3 -right-1 absolute z-index-100 scale-[0.8] rounded-4 bg-gradient-to-t from-[rgba(76,99,255,0.48)] to-[rgba(76,99,255,0.48)] px-[6px] py-1 text-system-white text-xs">
            {t('create.createForm.settings.freeTrials', {
              count: option.remainingTrials,
              s: option.remainingTrials > 1 ? 's' : '',
              es: option.remainingTrials > 1 ? 'es' : '',
            })}
          </div>
        ) : undefined,
      }))}
      onValueChange={(nextValue) => {
        if (
          checkOptionValue?.({
            key: 'resolution',
            value: nextValue,
            subsPlan: userInfo?.subs_plan as SubsPlanType,
          })
        ) {
          onShouldUpgrade?.('resolution');
          return;
        }

        onChange(nextValue);
      }}
    />
  );
};

export const ResolutionOptionSetting: FC<ToolSettingOptionProps<string>> = ({
  value,
  onChange,
  onShouldUpgrade,
  checkOptionValue,
  className,
  options,
}) => {
  const t = useTranslations();

  return (
    <ToolSettingContainer title={<span>{t('create.createForm.settings.resolution.title')}</span>} className={className}>
      <div className="flex items-center gap-2">
        <ResolutionOption
          value={value}
          onChange={onChange}
          onShouldUpgrade={onShouldUpgrade}
          checkOptionValue={checkOptionValue}
          options={options ?? []}
        />
      </div>
    </ToolSettingContainer>
  );
};

export const AmountOption: FC<ToolSettingOptionProps<number>> = ({
  value,
  onChange,
  onShouldUpgrade,
  className,
  options,
  checkOptionValue,
}) => {
  const t = useTranslations();
  const userInfo = useStore((state) => state.userInfo);

  return (
    <ToolSettingContainer title={t('create.createForm.settings.amount.title')} className={className}>
      <ToolSettingsRadioGroup
        value={(value || 1).toString()}
        options={options.map((option) => ({
          value: option.value,
          label: option.label,
          isVipOption: option.showVip,
        }))}
        onValueChange={(nextValue) => {
          if (
            checkOptionValue?.({
              key: 'amount',
              value: nextValue,
              subsPlan: userInfo?.subs_plan as SubsPlanType,
            })
          ) {
            onShouldUpgrade?.('amount');
            return;
          }

          onChange(Number.parseInt(nextValue, 10));
        }}
      />
    </ToolSettingContainer>
  );
};

export const ScenarioOptionSetting: FC<ToolSettingOptionProps<string>> = ({ value, onChange, options, className }) => {
  const t = useTranslations();
  return (
    <ToolSettingContainer
      title={<span>{t('tools.oneClickVideoProduction.scenario.label')}</span>}
      className={className}
    >
      <ToolSettingsRadioGroup
        activeMode="item"
        value={value || ''}
        options={options.map((option) => ({
          value: option.value,
          label: option.label,
          description: option.description,
        }))}
        onValueChange={(nextValue) => onChange(nextValue)}
        className="gap-2 rounded-12 bg-transparent"
        optionsWrapperClassName="h-auto rounded-12 border border-system-white08 bg-system-white04 hover:bg-system-white08"
        activeOptionClassName="border-[#5765f2] bg-[rgba(87,101,242,0.20)] !rounded-12"
        labelClassName="py-4"
        renderContent={(option) => {
          return (
            <div className="flex w-full items-center gap-2 px-3 text-left">
              <span className="font-medium text-sm text-system-white leading-[1.4]">{option.label}</span>
              {option?.description && (
                <CustomTooltip content={option.description} side="top" trigger="hover" variant="glass">
                  <div>
                    <BaseLineInfo className="size-4 text-system-white48 hover:text-system-white" />
                  </div>
                </CustomTooltip>
              )}
            </div>
          );
        }}
      />
    </ToolSettingContainer>
  );
};

export const VoiceLanguageOptionSetting: FC<ToolSettingOptionProps<string>> = ({
  value,
  onChange,
  options,
  className,
  title,
}) => {
  return (
    <ToolSettingContainer title={title} className={className}>
      <ToolSettingsRadioGroup
        activeMode="item"
        value={value || ''}
        options={options.map((option) => ({ value: option.value, label: option.label }))}
        onValueChange={(nextValue) => onChange(nextValue)}
        className="gap-2 rounded-12 bg-transparent"
        optionsWrapperClassName="h-auto rounded-12 border border-system-white08 bg-system-white04 hover:bg-system-white08"
        activeOptionClassName="border-[#5765f2] bg-[rgba(87,101,242,0.20)] !rounded-12"
        labelClassName="py-4"
      />
    </ToolSettingContainer>
  );
};

export const SwitchOptionSetting: FC<Partial<ToolSettingOptionProps<boolean>>> = ({
  value,
  onChange,
  className,
  title,
}) => {
  return (
    <div className={className}>
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm text-system-white">{title}</span>
        <Switch className="h-7 w-12 p-0.5" checked={value ?? true} onCheckedChange={onChange} />
      </div>
    </div>
  );
};
